import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import HistoryIcon from "@mui/icons-material/History";
import api from "../services/ApiService";

const COLLAPSED_W = 60;
const EXPANDED_W  = 224;

function formatModelName(modelName) {
  const words = modelName.match(/[A-Z][a-z]*/g) || [];
  return words.join(" ") + " Table";
}

function truncateValue(value, length = 28) {
  if (!value) return "";
  return value.length > length ? value.slice(0, length) + "…" : value;
}

const Sidebar2 = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [logs, setLogs] = useState([]);

  const fetchLogs = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await api.get("/logs/");
      const filteredLogs = res.data
        .filter((log) => String(log.updated_by) === String(user.id))
        .slice(0, 10);
      setLogs(user.department === "Admin" ? res.data : filteredLogs);
    } catch (err) {
      console.error("Error fetching logs", err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const formatFieldName = (field) =>
    field
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  return (
    /* Flex placeholder */
    <Box
      sx={{
        width: isCollapsed ? COLLAPSED_W : EXPANDED_W,
        flexShrink: 0,
        transition: "width 0.25s ease",
      }}
    >
      {/* Fixed panel */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: isCollapsed ? COLLAPSED_W : EXPANDED_W,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #0b1e3d 0%, #0d2b5e 55%, #0b1e3d 100%)",
          borderLeft: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.35)",
          borderRadius: "14px 0 0 14px",
          overflow: "hidden",
          transition: "width 0.25s ease",
          zIndex: 1200,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 1.5,
            px: 1,
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            flexShrink: 0,
            gap: 1,
          }}
        >
          {isCollapsed ? (
            <HistoryIcon sx={{ color: "rgba(255,255,255,0.6)", fontSize: 20 }} />
          ) : (
            <>
              <HistoryIcon sx={{ color: "#60a5fa", fontSize: 16 }} />
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  letterSpacing: "0.4px",
                  whiteSpace: "nowrap",
                }}
              >
                Recent Logs
              </Typography>
            </>
          )}
        </Box>

        {/* ── Logs (scrollable) ── */}
        <Box
          sx={{
            flex: 1,
            overflowY: isCollapsed ? "hidden" : "auto",
            overflowX: "hidden",
            py: 1,
            px: isCollapsed ? 0 : 1,
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255,255,255,0.18) transparent",
            "&::-webkit-scrollbar": { width: "3px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.18)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "rgba(255,255,255,0.38)",
            },
          }}
        >
          {!isCollapsed && (
            <>
              {logs.length === 0 ? (
                <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem", textAlign: "center", mt: 3 }}>
                  No logs found
                </Typography>
              ) : (
                logs.map((log) => {
                  const isDeleted = log.field_name === "__deleted__";
                  return (
                    <Box
                      key={log.id}
                      sx={{
                        mb: 1,
                        p: 1.25,
                        borderRadius: "9px",
                        background: isDeleted
                          ? "rgba(248,113,113,0.08)"
                          : "rgba(255,255,255,0.04)",
                        border: isDeleted
                          ? "1px solid rgba(248,113,113,0.3)"
                          : "1px solid rgba(255,255,255,0.06)",
                      }}
                    >
                      {/* Who + what */}
                      <Typography
                        sx={{ color: "#fff", fontSize: "0.72rem", fontWeight: 600, lineHeight: 1.4, mb: 0.5 }}
                      >
                        {log.name || "System"}{" "}
                        <span style={{ fontWeight: 400, color: "rgba(255,255,255,0.65)" }}>
                          {isDeleted ? "deleted" : "edited"}
                        </span>{" "}
                        <span style={{ color: "#93c5fd" }}>
                          {formatModelName(log.model_name)}
                        </span>{" "}
                        <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 400 }}>
                          #{log.record_id}
                        </span>
                      </Typography>

                      {/* Field change */}
                      {!isDeleted && (
                        <Box sx={{ fontSize: "0.68rem", lineHeight: 1.5 }}>
                          <Box sx={{ color: "rgba(255,255,255,0.5)", mb: 0.25 }}>
                            {formatFieldName(log.field_name)}
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            <Box
                              sx={{
                                background: "rgba(248,113,113,0.15)",
                                color: "#fca5a5",
                                px: 0.75,
                                py: 0.2,
                                borderRadius: "4px",
                                maxWidth: "100%",
                                wordBreak: "break-all",
                              }}
                            >
                              {truncateValue(log.old_value) || "—"}
                            </Box>
                            <Box sx={{ color: "rgba(255,255,255,0.3)", alignSelf: "center" }}>→</Box>
                            <Box
                              sx={{
                                background: "rgba(74,222,128,0.12)",
                                color: "#86efac",
                                px: 0.75,
                                py: 0.2,
                                borderRadius: "4px",
                                maxWidth: "100%",
                                wordBreak: "break-all",
                              }}
                            >
                              {truncateValue(log.new_value) || "—"}
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Timestamp */}
                      <Typography
                        sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.65rem", mt: 0.75 }}
                      >
                        {new Date(log.updated_at).toLocaleString()}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </>
          )}
        </Box>

        {/* ── Toggle button ── */}
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            px: 0.75,
            py: 1,
            flexShrink: 0,
          }}
        >
          <Box
            onClick={() => setIsCollapsed((c) => !c)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: isCollapsed ? "center" : "flex-start",
              gap: 1,
              px: isCollapsed ? 0 : 1.5,
              py: 0.85,
              borderRadius: "9px",
              cursor: "pointer",
              color: "rgba(255,255,255,0.55)",
              transition: "all 0.18s ease",
              "&:hover": {
                background: "rgba(255,255,255,0.08)",
                color: "#fff",
              },
            }}
          >
            {isCollapsed ? (
              <ChevronLeftIcon fontSize="small" />
            ) : (
              <>
                <ChevronRightIcon fontSize="small" />
                <Box sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>Collapse</Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar2;
